import React, { useEffect, useRef, useState } from "react";
import { Answers, attribution, capitals, intentions, zones } from "./model";
import { LeadSubmissionError, Submission, submitWebLead } from "./hubspotTransport";
import { bookingUrl } from "./meeting";

const KEY = "franquicias-live-contact-v1";
const PREPARING = "Estamos preparando tu solicitud para que puedas agendar.";
const fields = {
  firstname: ["Nombre", "given-name", "text"],
  lastname: ["Apellidos", "family-name", "text"],
  email: ["Correo electrónico", "email", "email"],
  phone: ["Teléfono", "tel", "tel"],
} as const;
type Contact = { firstname: string; lastname: string; email: string; phone: string; consent: boolean };
const empty: Contact = { firstname: "", lastname: "", email: "", phone: "", consent: false };
const normalized = (s: string) => s.trim().replace(/\s+/g, " ");
export function contactIssues(c: Contact) {
  return {
    firstname: !normalized(c.firstname) || c.firstname.length > 100, lastname: !normalized(c.lastname) || c.lastname.length > 100,
    email: !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(c.email.trim()),
    phone: !/^\+?[1-9]\d{9,14}$/.test(c.phone.replace(/[\s().-]/g, "")),
    consent: !c.consent,
  };
}
function restoredRequest(value: unknown): value is Submission {
  if (!value || typeof value !== "object") return false;
  const s = value as Submission;
  return s.version === 2 && typeof s.requestId === "string" && /^[a-f\d]{8}-[a-f\d]{4}-4[a-f\d]{3}-[89ab][a-f\d]{3}-[a-f\d]{12}$/i.test(s.requestId) &&
    Object.keys(fields).every(k => typeof s[k as keyof Submission] === "string") &&
    !Object.values(contactIssues(s)).some(Boolean) && s.email.length <= 254 && s.phone.length <= 32 &&
    intentions.slice(0, 2).includes(s.intencion) && capitals.includes(s.capital) && zones.includes(s.zona) &&
    s.consentVersion === "frq-contact-v1" && !!s.utm && typeof s.utm === "object" && !Array.isArray(s.utm) &&
    Object.entries(s.utm).every(([key, value]) => /^utm_(source|medium|campaign|content|term|id)$/.test(key) && typeof value === "string" && value.length <= 200);
}
export default function LiveContactAgenda({ answers, onBack }: { answers: Answers; onBack: () => void }) {
  const [contact, setContact] = useState<Contact>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof Contact, boolean>>>({});
  const [ready, setReady] = useState(false), [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false), [message, setMessage] = useState("");
  const [calendarReady, setCalendarReady] = useState(false), [preparingText, setPreparingText] = useState("");
  const [locked, setLocked] = useState(false);
  const container = useRef<HTMLDivElement>(null), submitting = useRef(false), mounted = useRef(false);
  const request = useRef<Submission | null>(null);
  useEffect(() => {
    mounted.current = true;
    try {
      const d = JSON.parse(sessionStorage.getItem(KEY) || "null");
      if (d?.version === 1 && Date.now() - d.savedAt < 86400000 && d.savedAt <= Date.now() &&
        d.contact && Object.keys(fields).every(k => typeof d.contact[k] === "string" && d.contact[k].length <= 254) && typeof d.contact.consent === "boolean") {
        setContact(d.contact);
        if (restoredRequest(d.request)) {
          request.current = d.request;
          setContact({ firstname: d.request.firstname, lastname: d.request.lastname, email: d.request.email, phone: d.request.phone, consent: true });
          setLocked(true);
          setAccepted(d.accepted === true);
          if (!d.accepted) setMessage("Hay un envío pendiente de comprobar. Pulsa verificar para consultar su recepción sin duplicarlo.");
        }
      }
    } catch { /* Storage may be unavailable or corrupt. */ }
    setReady(true);
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    if (!ready) return;
    try { sessionStorage.setItem(KEY, JSON.stringify({ version: 1, savedAt: Date.now(), contact, request: request.current, accepted })); } catch {}
  }, [ready, contact, busy, accepted]);
  useEffect(() => {
    if (!accepted) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let letters = 0;
    setPreparingText(reducedMotion ? PREPARING : "");
    const typing = reducedMotion ? undefined : window.setInterval(() => {
      letters += 1;
      setPreparingText(PREPARING.slice(0, letters));
      if (letters >= PREPARING.length) window.clearInterval(typing);
    }, 22);
    const reveal = window.setTimeout(() => {
      window.clearInterval(typing);
      setCalendarReady(true);
      setMessage("Tu solicitud fue recibida. Elige ahora un horario en el calendario.");
    }, 1500);
    return () => { window.clearInterval(typing); window.clearTimeout(reveal); };
  }, [accepted]);
  const submit = async () => {
    if (!ready || submitting.current || accepted) return;
    const issues = contactIssues(contact);
    setErrors(issues);
    const first = (Object.keys(issues) as (keyof Contact)[]).find(k => issues[k]);
    if (first) {
      container.current?.querySelector<HTMLInputElement>("#live-" + first)?.focus();
      return;
    }
    if (!request.current) {
      request.current = {
        version: 2, requestId: crypto.randomUUID(),
        firstname: normalized(contact.firstname), lastname: normalized(contact.lastname),
        email: contact.email.trim().toLowerCase(), phone: contact.phone.replace(/[\s().-]/g, ""),
        intencion: answers.intencion, capital: answers.capital, zona: answers.zona,
        consent: true, consentVersion: "frq-contact-v1", utm: attribution(location.href),
      };
    }
    submitting.current = true;
    // Persist before the network call so an immediate reload can recover the same request.
    try { sessionStorage.setItem(KEY, JSON.stringify({ version: 1, savedAt: Date.now(), contact, request: request.current, accepted: false })); } catch {}
    setBusy(true); setLocked(true); setMessage("");
    try {
      await submitWebLead(request.current);
      if (mounted.current) { setAccepted(true); setMessage(""); }
    } catch (error) {
      if (!mounted.current) return;
      const e = error as LeadSubmissionError;
      setMessage(e instanceof LeadSubmissionError ? e.message : "No pudimos comprobar la recepción. Pulsa verificar; se usará la misma solicitud para evitar duplicados.");
      // Only definitive rejection allows editing and a new request. Unknown outcomes keep the original ID.
      if (["invalid_fields", "invalid_consent", "provider_rejected", "integration_disabled", "configuration_required", "origin_not_allowed"].includes(e.code)) {
        request.current = null; setLocked(false);
      }
    } finally { submitting.current = false; if (mounted.current) setBusy(false); }
  };
  const meeting = bookingUrl({
    firstname: request.current?.firstname || normalized(contact.firstname),
    lastname: request.current?.lastname || normalized(contact.lastname),
    email: request.current?.email || contact.email.trim().toLowerCase(),
  });
  return <div ref={container} aria-busy={busy} onKeyDown={e => {
    if (e.key === "Enter" && e.target instanceof HTMLInputElement && e.target.type !== "checkbox") { e.preventDefault(); void submit(); }
  }}>
    {!accepted ? <>
      <p className="fhelp">Envía tu solicitud para continuar al calendario. La reunión se reserva al confirmarla en HubSpot.</p>
      <div className="fields">
        {(Object.keys(fields) as (keyof typeof fields)[]).map(name => <div className={"fld" + (errors[name] ? " err" : "")} key={name}>
          <label htmlFor={"live-" + name}>{fields[name][0]}</label>
          <input id={"live-" + name} name={name} type={fields[name][2]} autoComplete={fields[name][1]} required
            maxLength={name === "email" ? 254 : name === "phone" ? 32 : 100} value={contact[name]} readOnly={locked}
            aria-invalid={!!errors[name]} aria-describedby={errors[name] ? "live-error-" + name : undefined}
            onChange={e => { const value = { ...contact, [name]: e.target.value }; setContact(value); if (errors[name]) setErrors(old => ({ ...old, [name]: contactIssues(value)[name] })); }} />
          <span className="msg" id={"live-error-" + name}>{name === "phone" ? "Incluye un teléfono válido con código de país." : name === "email" ? "Escribe un correo válido." : "Completa este campo."}</span>
        </div>)}
      </div>
      <label className={"consent" + (errors.consent ? " err" : "")} htmlFor="live-consent">
        <input id="live-consent" type="checkbox" required checked={contact.consent} disabled={locked} aria-invalid={!!errors.consent}
          onChange={e => { setContact(c => ({ ...c, consent: e.target.checked })); setErrors(old => ({ ...old, consent: !e.target.checked })); }} />
        <span>Sí, quiero que REMAX República Dominicana me contacte por llamada, WhatsApp o correo para coordinar una reunión de presentación sobre la franquicia. <a href="/politica-privacidad" target="_blank" rel="noreferrer">Política de privacidad</a>.</span>
      </label>
      {errors.consent && <p role="alert">Confirma tu consentimiento para continuar.</p>}
      <div className="fnav">
        <button type="button" className="back" disabled={locked || busy} onClick={onBack}>Atrás</button>
        <button type="button" className="btn btn-1" disabled={!ready || busy} onClick={() => void submit()}>{busy ? "Enviando…" : locked ? "Verificar recepción" : "Enviar y elegir horario"}</button>
      </div>
    </> : !calendarReady ? <p className="fhelp" role="status" aria-label={PREPARING} style={{ minHeight: "3em" }}>
      <span aria-hidden="true">{preparingText}</span>
    </p> : <>
      <h4>Elige el horario de tu reunión</h4>
      <p className="fhelp">Calendario de Melissa Zabala · 30 minutos. La confirmación de la reserva aparecerá dentro del calendario. Usa el mismo correo de tu solicitud: <strong>{request.current?.email}</strong>.</p>
      <iframe title="Calendario de reuniones de Melissa Zabala" src={meeting} className="frq-meeting" referrerPolicy="no-referrer" />
      <p className="micro">Si el calendario no carga, <a href={meeting} target="_blank" rel="noreferrer">abre la agenda en otra pestaña</a>. Tu solicitud ya está registrada; no necesitas enviarla otra vez.</p>
    </>}
    {message && <p role="status" aria-live="polite" className="fhelp">{message}</p>}
  </div>;
}
